import { sleep } from "@/lib/utils"

import type { Api, FeedBlockAdmin, PersonalFeedSettings } from "../types"

// Единая управляемая выдача главной (fd-feed-01). Мок-хранилище блоков одного
// плейсмента: список упорядочен по position, reorder целиком переписывает
// порядок. Тот же контракт реализует http-провайдер поверх gateway.
let blocks: FeedBlockAdmin[] = [
  {
    id: "feed_demo_campaign",
    placement: "home",
    department: "clothes",
    segment: null,
    kind: "campaign",
    position: 0,
    title: "Новый сезон",
    description: "Главный баннер витрины",
    action: { kind: "link", label: "Смотреть" },
    payload: {
      slides: [
        {
          id: "sl_1",
          imageUrl: "https://picsum.photos/seed/osmix-campaign/1200/600",
          title: "Осень 2026",
          description: "Свежие коллекции недели",
          collectionSlug: "autumn-2026",
        },
      ],
    },
    published: true,
    createdAt: "2026-08-01T10:00:00Z",
    updatedAt: "2026-08-01T10:00:00Z",
  },
  {
    id: "feed_demo_categories",
    placement: "home",
    department: "clothes",
    segment: "womens",
    kind: "categoryRow",
    position: 1,
    title: "Категории",
    description: null,
    action: { kind: "chevron" },
    payload: {
      items: [
        {
          id: "tl_1",
          imageUrl: "https://picsum.photos/seed/osmix-dresses/400/400",
          label: "Платья",
          categoryId: "cat_dresses",
        },
        {
          id: "tl_2",
          imageUrl: "https://picsum.photos/seed/osmix-shoes/400/400",
          label: "Обувь",
          categoryId: "cat_shoes",
        },
      ],
    },
    published: true,
    createdAt: "2026-08-01T10:05:00Z",
    updatedAt: "2026-08-01T10:05:00Z",
  },
  {
    id: "feed_demo_personal",
    placement: "home",
    department: "clothes",
    segment: null,
    kind: "personalFeed",
    position: 2,
    title: "Для вас",
    description: "Персональная подборка",
    action: null,
    payload: {},
    published: false,
    createdAt: "2026-08-01T10:10:00Z",
    updatedAt: "2026-08-01T10:10:00Z",
  },
]

const ordered = (placement: FeedBlockAdmin["placement"]) =>
  blocks.filter((block) => block.placement === placement).sort((a, b) => a.position - b.position)

export const feed: Api["feed"] = {
  async list(placement) {
    await sleep(150)
    return ordered(placement)
  },

  async create(input) {
    await sleep(180)
    const placement = input.placement ?? "home"
    const now = new Date().toISOString()
    const position = ordered(placement).length
    const block: FeedBlockAdmin = {
      id: `feed_${crypto.randomUUID()}`,
      placement,
      department: input.department ?? "clothes",
      segment: input.segment ?? null,
      kind: input.kind,
      position,
      title: input.title ?? null,
      description: input.description ?? null,
      action: input.action ?? null,
      payload: input.payload ?? {},
      published: input.published ?? true,
      createdAt: now,
      updatedAt: now,
    }
    blocks = [...blocks, block]
    return block
  },

  async update(id, patch) {
    await sleep(150)
    const now = new Date().toISOString()
    let updated: FeedBlockAdmin | undefined
    blocks = blocks.map((block) => {
      if (block.id !== id) return block
      updated = {
        ...block,
        department: patch.department !== undefined ? patch.department : block.department,
        segment: patch.segment !== undefined ? patch.segment : block.segment,
        title: patch.title !== undefined ? patch.title : block.title,
        description: patch.description !== undefined ? patch.description : block.description,
        action: patch.action !== undefined ? patch.action : block.action,
        payload: patch.payload !== undefined ? patch.payload : block.payload,
        published: patch.published !== undefined ? patch.published : block.published,
        updatedAt: now,
      }
      return updated
    })
    if (!updated) throw new Error("Feed block not found")
    return updated
  },

  async remove(id) {
    await sleep(150)
    const target = blocks.find((block) => block.id === id)
    blocks = blocks.filter((block) => block.id !== id)
    // Уплотняем позиции оставшихся блоков плейсмента, чтобы список оставался
    // 0..n-1 без дыр — как это делает бэкенд после удаления.
    if (target) {
      ordered(target.placement).forEach((block, index) => {
        block.position = index
      })
    }
  },

  async reorder(placement, orderedIds) {
    await sleep(180)
    const now = new Date().toISOString()
    const inPlacement = blocks.filter((block) => block.placement === placement)
    const byId = new Map(inPlacement.map((block) => [block.id, block]))
    const next: FeedBlockAdmin[] = []
    for (const id of orderedIds) {
      const found = byId.get(id)
      if (found) next.push(found)
    }
    for (const block of inPlacement) {
      if (!orderedIds.includes(block.id)) next.push(block)
    }
    next.forEach((block, index) => {
      block.position = index
      block.updatedAt = now
    })
    return ordered(placement)
  },

  async uploadMedia(file) {
    await sleep(200)
    // Офлайн: отдаём локальный object-URL, чтобы предпросмотр показал картинку.
    return URL.createObjectURL(file)
  },

  async personalSettings() {
    await sleep(100)
    return personalSettings
  },

  async updatePersonalSettings(patch) {
    await sleep(150)
    personalSettings = {
      ...personalSettings,
      ...patch,
      weights: { ...personalSettings.weights, ...(patch.weights ?? {}) },
    }
    return personalSettings
  },
}

// Офлайн-копия настроек персональной ленты (п.16 правок 27.10).
let personalSettings: PersonalFeedSettings = {
  enabled: true,
  weights: { purchase: 10, cart: 5, favorite: 4, shopFollow: 3, view: 1.5, impression: -0.15 },
  halfLifeDays: 21,
  explorationShare: 0.15,
  maxSameShopInRow: 2,
}
